import type { AvatarAppearance, CrowdAvatar } from './types'
import { KAYKIT_CHARACTERS } from './characterCatalog'
const SKIN_TONES = ['#f5c6a5', '#e0ac69', '#c68642', '#8d5524', '#ffd5b8', '#d4a574']
const HAIR_COLORS = ['#1a1a1a', '#3d2314', '#6b3a2a', '#c4a35a', '#e85d04', '#7b2cbf', '#00b4d8', '#ff006e']
const TOP_COLORS = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a29bfe', '#fd79a8', '#00b894', '#0984e3', '#e17055']
const BOTTOM_COLORS = ['#2d3436', '#636e72', '#0984e3', '#6c5ce7', '#2ecc71', '#341f97']

function pick<T>(arr: readonly T[], rnd: () => number): T {
  const item = arr[Math.floor(rnd() * arr.length)]
  if (item === undefined) {
    throw new Error('pick: empty array')
  }
  return item
}

/** Mulberry32 — стабильный RNG от seed (одинаковый id → тот же скин). */
export function createSeededRng(seed: string): () => number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return () => {
    h |= 0
    h = (h + 0x6d2b79f5) | 0
    let t = Math.imul(h ^ (h >>> 15), 1 | h)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function randomAppearance(seed: string): AvatarAppearance {
  const rnd = createSeededRng(seed)
  const hairStyles = ['short', 'spiky', 'afro', 'pony', 'bald', 'mohawk'] as const
  const eyeStyles = ['dot', 'big', 'wink', 'anime'] as const
  const mouthStyles = ['smile', 'grin', 'o', 'flat'] as const
  const browStyles = ['none', 'angry', 'unibrow'] as const
  const hats = ['none', 'cap', 'beanie', 'propeller', 'crown'] as const
  const accessories = ['none', 'backpack', 'scarf', 'glasses'] as const
  const sleeves = ['short', 'long', 'tank'] as const

  return {
    characterId: pick(KAYKIT_CHARACTERS, rnd),
    baseSkin: pick(['survivorMaleB', 'survivorFemaleA', 'zombieA', 'zombieC'] as const, rnd),
    tint: pick(['#ffffff', '#ffe0e0', '#e0ffe8', '#e0f0ff', '#fff3c4'] as const, rnd),
    skinTone: pick(SKIN_TONES, rnd),
    hairColor: pick(HAIR_COLORS, rnd),
    hairStyle: pick(hairStyles, rnd),
    bodyScale: 0.88 + rnd() * 0.28,
    face: {
      eyeStyle: pick(eyeStyles, rnd),
      mouthStyle: pick(mouthStyles, rnd),
      blush: rnd() > 0.55,
      browStyle: pick(browStyles, rnd),
    },
    clothes: {
      topColor: pick(TOP_COLORS, rnd),
      bottomColor: pick(BOTTOM_COLORS, rnd),
      sleeveStyle: pick(sleeves, rnd),
      hat: pick(hats, rnd),
      accessory: pick(accessories, rnd),
    },
    equipment: {
      mainHand: 'none',
      offHand: 'none',
      showHat: rnd() > 0.25,
      showCape: rnd() > 0.3,
      showExtra: rnd() > 0.35,
    },
  }
}

const DEMO_NAMES = [
  'Bean', 'Nugget', 'Pickle', 'Mochi', 'Bloop', 'Ziggy', 'Waffle', 'Pixel',
  'Chuck', 'Sprout', 'Goober', 'Tater', 'Noodle', 'Biscuit', 'Yoink', 'Fizz',
  'Nova', 'Raven', 'Echo', 'Bolt', 'Maple', 'Koda', 'Pepper', 'Orbit',
]

export function createDemoCrowd(count = 14): CrowdAvatar[] {
  return Array.from({ length: count }, (_, i) => {
    const id = `demo-avatar-${i}`
    const displayName =
      count > 64
        ? `${DEMO_NAMES[i % DEMO_NAMES.length] ?? 'Fan'}${i}`
        : (DEMO_NAMES[i % DEMO_NAMES.length] ?? `Avatar ${i}`)
    return {
      id,
      displayName,
      appearance: randomAppearance(id),
    }
  })
}
